import axios from 'axios';

// ─── Axios Instances ─────────────────────────────────────────────────────────
const StationsAPI = axios.create({
  baseURL: 'https://yaatri-5v36.onrender.com/api/stations',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

const SchedulesAPI = axios.create({
  baseURL: 'https://yaatri-5v36.onrender.com/api/schedules',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Station Types ───────────────────────────────────────────────────────────
export interface StationFull {
  _id: string;
  code: string;
  name: string;
  state: string;
  zone: string;
  address?: string;
  geometry?: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  imported_at?: string;
}

export interface StationPaginatedResponse {
  success: boolean;
  total: number;
  page: number;
  totalPages: number;
  data: StationFull[];
}

export interface StationStatsData {
  total: number;
  states: { _id: string; count: number }[];
  zones: { _id: string; count: number }[];
}

export interface StationsQuery {
  page?: number;
  limit?: number;
  search?: string;
  zone?: string;
  state?: string;
}

// ─── Schedule Types ──────────────────────────────────────────────────────────

/**
 * Raw schedule entry from DB (used in basic endpoints).
 */
export interface ScheduleEntry {
  _id: string;
  id: number;
  train_number: string;
  train_name: string;
  station_code: string;
  station_name: string;
  arrival: string;      // e.g. "14:30" or "--:--"
  departure: string;    // e.g. "14:35" or "--:--"
  day: number;          // journey day offset (1-based) OR weekday index
  station_details?: StationFull;
}

export interface SchedulePaginatedResponse {
  success: boolean;
  total: number;
  page: number;
  totalPages: number;
  data: ScheduleEntry[];
}

export interface TrainScheduleResponse {
  success: boolean;
  train_number: string;
  train_name: string;
  total_stops: number;
  data: ScheduleEntry[];
}

export interface ScheduleStatsData {
  total: number;
  trainCount: number;
  stationCount: number;
}

export interface SchedulesQuery {
  page?: number;
  limit?: number;
  search?: string;
  day?: number;
}

// ─── Direct Train Types ──────────────────────────────────────────────────────

export interface DirectTrainStop {
  station_code: string;
  station_name: string;
  departure?: string;
  arrival?: string;
  day_offset: number;
  date: string;        // YYYY-MM-DD resolved calendar date
  weekday: string;
}

export interface DirectTrainResult {
  train_number: string;
  train_name: string;
  from: DirectTrainStop;
  to: DirectTrainStop;
  duration: {
    total_minutes: number;
    formatted: string;   // "Xh Ym"
  };
}

/**
 * GET /api/schedules/direct
 * Response shape from getDirectTrains controller.
 */
export interface DirectTrainResponse {
  success: boolean;
  from_station: StationFull | { code: string };
  to_station: StationFull | { code: string };
  travel_date: string;
  travel_weekday: string;
  count: number;
  data: DirectTrainResult[];
}

// ─── Connecting Train Types ──────────────────────────────────────────────────

export interface ConnectingLegStop {
  station_code: string;
  station_name: string;
  departure?: string;
  arrival?: string;
  date: string;
  weekday: string;
}

export interface ConnectingLeg {
  train_number: string;
  train_name: string;
  from: ConnectingLegStop;
  to: ConnectingLegStop;
  duration_mins: number;
}

export interface TransferInfo {
  station_code: string;
  station_name: string;
  transfer_mins: number;
  formatted: string;  // "Xh Ym"
}

export interface ConnectingTrainResult {
  leg_1: ConnectingLeg;
  transfer: TransferInfo;
  leg_2: ConnectingLeg;
  total: {
    total_minutes: number;
    formatted: string;
  };
}

/**
 * GET /api/schedules/connecting
 * Response shape from getConnectingTrains controller.
 */
export interface ConnectingTrainResponse {
  success: boolean;
  from_station: StationFull | { code: string };
  to_station: StationFull | { code: string };
  travel_date: string;
  travel_weekday: string;
  min_transfer_mins: number;
  has_direct_trains: boolean;
  total_connections_found: number;
  count: number;
  data: ConnectingTrainResult[];
}

// ─── Weekday Train Types ─────────────────────────────────────────────────────

export interface WeekdayTrainResult {
  train_number: string;
  train_name: string;
  weekday: string;
  from: { station_code: string; station_name: string; departure: string };
  to: { station_code: string; station_name: string; arrival: string };
  duration: { total_minutes: number; formatted: string };
}

/**
 * GET /api/schedules/by-weekday
 */
export interface WeekdayTrainResponse {
  success: boolean;
  weekday: string;
  from_station_code: string;
  to_station_code: string;
  count: number;
  data: WeekdayTrainResult[];
}

// ─── Timeline Types ──────────────────────────────────────────────────────────

export interface TimelineStop {
  stop_number: number;
  station_code: string;
  station_name: string;
  arrival: string;
  departure: string;
  day_offset: number;
  date: string;
  weekday: string;
}

/**
 * GET /api/schedules/train/:trainNumber/timeline
 */
export interface TrainTimelineResponse {
  success: boolean;
  train_number: string;
  train_name: string;
  start_date: string;
  total_stops: number;
  data: TimelineStop[];
}

// ─── Station Board Types ─────────────────────────────────────────────────────

export interface BoardEntry {
  train_number: string;
  train_name: string;
  departure: string;
  arrival: string;
  day: number;
  date: string;
  weekday: string;
}

/**
 * GET /api/schedules/station/:stationCode/board
 */
export interface StationBoardResponse {
  success: boolean;
  station: StationFull | { code: string };
  date: string;
  weekday: string;
  window_hours: number;
  count: number;
  data: BoardEntry[];
}

// ─── Compare Trains Types ────────────────────────────────────────────────────

export interface CompareTrainResult {
  train_number: string;
  found: boolean;
  train_name?: string;
  origin?: { station_code: string; station_name: string; departure: string; day: number };
  destination?: { station_code: string; station_name: string; arrival: string; day: number };
  total_stops?: number;
  total_duration?: { total_minutes: number; formatted: string } | null;
  stops?: ScheduleEntry[];
}

/**
 * GET /api/schedules/compare
 */
export interface CompareTrainsResponse {
  success: boolean;
  compared_trains: string[];
  data: CompareTrainResult[];
}

// ─── Station API Functions ───────────────────────────────────────────────────

export const fetchStationStats = async (): Promise<StationStatsData> => {
  const { data } = await StationsAPI.get<{ success: boolean; data: StationStatsData }>('/stats');
  return data.data;
};

export const fetchAllStations = async (params: StationsQuery = {}): Promise<StationPaginatedResponse> => {
  const { data } = await StationsAPI.get<StationPaginatedResponse>('/', {
    params: { limit: 50, ...params },
  });
  return data;
};

export const searchStationsAPI = async (q: string): Promise<StationFull[]> => {
  const { data } = await StationsAPI.get<{ success: boolean; data: StationFull[] }>('/search', {
    params: { q, limit: 20 },
  });
  return data.data ?? [];
};

export const fetchStationStates = async (): Promise<string[]> => {
  const { data } = await StationsAPI.get<{ success: boolean; data: string[] }>('/states');
  return data.data ?? [];
};

export const fetchStationZones = async (): Promise<string[]> => {
  const { data } = await StationsAPI.get<{ success: boolean; data: string[] }>('/zones');
  return data.data ?? [];
};

export const fetchStationByCode = async (code: string): Promise<StationFull> => {
  const { data } = await StationsAPI.get<{ success: boolean; data: StationFull }>(`/${code}`);
  return data.data;
};

export const fetchStationsByState = async (
  state: string,
  page = 1,
  limit = 50
): Promise<StationPaginatedResponse> => {
  const { data } = await StationsAPI.get<StationPaginatedResponse>(`/state/${state}`, {
    params: { page, limit },
  });
  return data;
};

export const fetchStationsByZone = async (
  zone: string,
  page = 1,
  limit = 50
): Promise<StationPaginatedResponse> => {
  const { data } = await StationsAPI.get<StationPaginatedResponse>(`/zone/${zone}`, {
    params: { page, limit },
  });
  return data;
};

// ─── Schedule API Functions ──────────────────────────────────────────────────

export const fetchScheduleStats = async (): Promise<ScheduleStatsData> => {
  const { data } = await SchedulesAPI.get<{ success: boolean; data: ScheduleStatsData }>('/stats');
  return data.data;
};

export const fetchAllSchedules = async (params: SchedulesQuery = {}): Promise<SchedulePaginatedResponse> => {
  const { data } = await SchedulesAPI.get<SchedulePaginatedResponse>('/', {
    params: { limit: 50, ...params },
  });
  return data;
};

export const fetchTrainSchedule = async (trainNumber: string): Promise<TrainScheduleResponse> => {
  const { data } = await SchedulesAPI.get<TrainScheduleResponse>(`/train/${trainNumber}`);
  return data;
};

export const fetchSchedulesByStation = async (
  stationCode: string,
  page = 1,
  limit = 50
): Promise<SchedulePaginatedResponse & { station?: StationFull }> => {
  const { data } = await SchedulesAPI.get(`/station/${stationCode}`, {
    params: { page, limit },
  });
  return data;
};

export const searchSchedulesAPI = async (train?: string, station?: string): Promise<ScheduleEntry[]> => {
  const { data } = await SchedulesAPI.get<{ success: boolean; data: ScheduleEntry[] }>('/search', {
    params: { train, station },
  });
  return data.data ?? [];
};

// ─── Advanced Search Functions ───────────────────────────────────────────────

/**
 * GET /api/schedules/direct
 * Returns direct trains between two stations on a specific date.
 * Backend returns: { success, from_station, to_station, travel_date, travel_weekday, count, data[] }
 * Each data item: { train_number, train_name, from: { departure, date, weekday, ... }, to: { arrival, date, weekday, ... }, duration }
 */
export const fetchDirectTrains = async (
  from: string,
  to: string,
  date: string,
  dayIsWeekday = false
): Promise<DirectTrainResponse> => {
  const { data } = await SchedulesAPI.get<DirectTrainResponse>('/direct', {
    params: { from, to, date, ...(dayIsWeekday && { day_is_weekday: 'true' }) },
  });
  return data;
};

/**
 * GET /api/schedules/connecting
 * Returns connecting train options (one change) between two stations.
 * Backend returns: { success, from_station, to_station, travel_date, min_transfer_mins,
 *                    has_direct_trains, total_connections_found, count, data[] }
 * Each data item: { leg_1: { train_number, from, to, duration_mins }, transfer: { transfer_mins, formatted }, leg_2, total }
 */
export const fetchConnectingTrains = async (
  from: string,
  to: string,
  date: string,
  minTransferMins = 30,
  maxResults = 20
): Promise<ConnectingTrainResponse> => {
  const { data } = await SchedulesAPI.get<ConnectingTrainResponse>('/connecting', {
    params: {
      from,
      to,
      date,
      min_transfer_mins: minTransferMins,
      max_results: maxResults,
    },
  });
  return data;
};

/**
 * GET /api/schedules/by-weekday
 * Returns trains between two stations on a recurring weekday.
 * Backend returns: { success, weekday, from_station_code, to_station_code, count, data[] }
 */
export const fetchTrainsByWeekday = async (
  from: string,
  to: string,
  weekday: string | number
): Promise<WeekdayTrainResponse> => {
  const { data } = await SchedulesAPI.get<WeekdayTrainResponse>('/by-weekday', {
    params: { from, to, weekday },
  });
  return data;
};

/**
 * GET /api/schedules/train/:trainNumber/timeline
 * Returns all stops with resolved calendar dates from a given start date.
 * Backend returns: { success, train_number, train_name, start_date, total_stops, data[] }
 * Each stop: { stop_number, station_code, station_name, arrival, departure, day_offset, date, weekday }
 */
export const fetchTrainTimeline = async (
  trainNumber: string,
  startDate: string
): Promise<TrainTimelineResponse> => {
  const { data } = await SchedulesAPI.get<TrainTimelineResponse>(
    `/train/${trainNumber}/timeline`,
    { params: { start_date: startDate } }
  );
  return data;
};

/**
 * GET /api/schedules/station/:stationCode/board
 * Returns trains departing from a station within a time window.
 * Backend returns: { success, station, date, weekday, window_hours, count, data[] }
 */
export const fetchStationDepartureBoard = async (
  stationCode: string,
  date: string,
  windowHours = 3
): Promise<StationBoardResponse> => {
  const { data } = await SchedulesAPI.get<StationBoardResponse>(
    `/station/${stationCode}/board`,
    { params: { date, window_hours: windowHours } }
  );
  return data;
};

/**
 * GET /api/schedules/compare
 * Side-by-side comparison for 2–5 trains.
 * Backend returns: { success, compared_trains, data[] }
 * Each item: { train_number, found, train_name?, origin?, destination?, total_stops?, total_duration?, stops? }
 */
export const compareTrainsAPI = async (trains: string[]): Promise<CompareTrainsResponse> => {
  const { data } = await SchedulesAPI.get<CompareTrainsResponse>('/compare', {
    params: { trains: trains.join(',') },
  });
  return data;
};

export interface NearbyStation {
  _id: string;
  code: string;
  name: string;
  state?: string;
  zone?: string;
  distance_km: number;
}

export interface NearbyStationsResponse {
  success: boolean;
  count: number;
  radius: number;
  data: NearbyStation[];
}

/**
 * GET /api/schedules/stations/nearby
 * Fetches all railway stations within a specific geo-proximity radius.
 */
export const fetchNearbyStations = async (
  lat: number,
  lng: number,
  radius = 50
): Promise<NearbyStationsResponse> => {
  // Using StationsAPI to match your application's global networking wrapper configuration
  const { data } = await StationsAPI.get<NearbyStationsResponse>(
    '/nearby',
    {
      params: {
        lat,
        lng,
        radius
      }
    }
  );
  return data;
};